"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { motion } from "framer-motion";
import { SiteNavbar } from "@/shared/ui/site-navbar";
import { SiteFooter } from "@/shared/ui/site-footer";
import { HomeCtaSection } from "@/features/home/presentation/components/home-cta-section";
import type { Assessment } from "@/features/assessment/types";
import type { AppLocale } from "@/core/i18n/config";
import type { SiteNavbarContent, SiteFooterContent } from "@/shared/types/site-shell-content";
import type { HomeCtaSectionContent } from "@/features/home/types/home-page-content";
import { AssessmentHeroSection } from "../components/assessment-hero-section";
import { AssessmentGridSection } from "../components/assessment-grid-section";
interface AssessmentListingPageProps {
  assessments: Assessment[];
  featured: Assessment[];
  navigation: SiteNavbarContent;
  footer: SiteFooterContent;
  ctaSection: HomeCtaSectionContent;
  locale: AppLocale;
}
export function AssessmentListingPage(t0) {
  const $ = _c(34);
  const {
    assessments,
    featured,
    navigation,
    footer,
    ctaSection,
    locale
  } = t0;
  let t1;
  if ($[0] !== navigation) {
    t1 = <SiteNavbar content={navigation} />;
    $[0] = navigation;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Image src="https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=2000&q=85" alt="Mental health assessment" fill={true} priority={true} sizes="100vw" className="object-cover" />;
    t3 = <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10" />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 0,
      y: 20
    };
    t5 = {
      opacity: 1,
      y: 0
    };
    t6 = {
      duration: 0.8
    };
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  const t7 = locale === "hi" ? "\u092E\u093E\u0928\u0938\u093F\u0915 \u0938\u094D\u0935\u093E\u0938\u094D\u0925\u094D\u092F \u0905\u0938\u0947\u0938\u092E\u0947\u0902\u091F" : "Mental Health Assessments";
  let t8;
  if ($[7] !== t7) {
    t8 = <span className="mb-3 inline-block rounded-full bg-white/20 px-3 py-1 text-[10px] font-semibold tracking-[0.16em] text-white uppercase backdrop-blur-sm">{t7}</span>;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = locale === "hi" ? "\u0905\u092A\u0928\u0940 \u092D\u093E\u0935\u0928\u093E\u0924\u094D\u092E\u0915 \u0938\u094D\u0925\u093F\u0924\u093F \u0915\u094B \u0938\u092E\u091D\u0947\u0902" : "Understand your emotional wellbeing";
  let t10;
  if ($[9] !== t9) {
    t10 = <h1 className="font-heading text-3xl leading-tight font-light tracking-tight text-white drop-shadow-md sm:text-4xl lg:text-5xl">{t9}</h1>;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const t11 = locale === "hi" ? "\u0939\u092E\u093E\u0930\u0940 \u0905\u0938\u0947\u0938\u092E\u0947\u0902\u091F\u094D\u0938 \u0906\u092A\u0915\u094B \u092F\u0939 \u091C\u093E\u0928\u0928\u0947 \u092E\u0947\u0902 \u092E\u0926\u0926 \u0915\u0930\u0924\u0940 \u0939\u0948\u0902 \u0915\u093F \u0906\u092A \u0915\u0948\u0938\u093E \u092E\u0939\u0938\u0942\u0938 \u0915\u0930 \u0930\u0939\u0947 \u0939\u0948\u0902 \u0914\u0930 \u0915\u094D\u092F\u093E \u0938\u092A\u094B\u0930\u094D\u091F \u0906\u092A\u0915\u0947 \u0932\u093F\u090F \u0938\u0939\u0940 \u0939\u094B \u0938\u0915\u0924\u093E \u0939\u0948\u0964" : "Our short, evidence-informed assessments help you understand how you are feeling and what kind of support may be right for you.";
  let t12;
  if ($[11] !== t11) {
    t12 = <p className="mt-3 max-w-xl text-sm leading-relaxed font-light text-white/80 drop-shadow-sm">{t11}</p>;
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t10 || $[14] !== t12 || $[15] !== t8) {
    t13 = <section className="relative flex h-[55vh] min-h-[400px] items-end overflow-hidden">{t2}{t3}<motion.div initial={t4} animate={t5} transition={t6} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">{t8}{t10}{t12}</motion.div></section>;
    $[13] = t10;
    $[14] = t12;
    $[15] = t8;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== featured || $[18] !== locale) {
    t14 = <AssessmentHeroSection featured={featured} locale={locale} />;
    $[17] = featured;
    $[18] = locale;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== assessments || $[21] !== locale) {
    t15 = <AssessmentGridSection assessments={assessments} locale={locale} />;
    $[20] = assessments;
    $[21] = locale;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== ctaSection) {
    t16 = <HomeCtaSection content={ctaSection} />;
    $[23] = ctaSection;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== footer) {
    t17 = <SiteFooter content={footer} />;
    $[25] = footer;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== t1 || $[28] !== t13 || $[29] !== t14 || $[30] !== t15 || $[31] !== t16 || $[32] !== t17) {
    t18 = <div className="relative min-h-screen overflow-x-hidden bg-background font-sans text-foreground">{t1}{t13}{t14}{t15}{t16}{t17}</div>;
    $[27] = t1;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  return t18;
}